<template>
  <v-container
    id="orders"
    tag="section"
  >
    <base-material-card
      color="indigo"
      icon="mdi-briefcase-outline"
      inline
      class="px-5 py-3"
      top-actions
    >
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">
          {{ $t('ResellerOrders') }}
        </div>
      </template>

      <v-row
        class="pt-4"
      >
        <v-col
          cols="12"
          lg="3"
          sm="6"
        >
          <v-autocomplete
            v-model="searchValueResellerUid"
            color="light-blue lighten-1"
            item-color="light-blue lighten-1"
            :label="$t('Pages.ResellerOrders.Form.Labels.ResellerFilter')"
            hide-details
            :items="resellers"
            prepend-icon="mdi-shopping"
            item-text="name"
            item-value="uid"
            clearable
            :disabled="loadingOrders"
            @click:clear="searchValueResellerUid = null"
          />
        </v-col>
        <v-col
          cols="12"
          lg="3"
          sm="6"
        >
          <v-autocomplete
            v-model="searchValueCustomerUid"
            color="teal lighten-1"
            item-color="teal lighten-1"
            :label="$t('Pages.ResellerOrders.Form.Labels.CustomerFilter')"
            hide-details
            :items="customers"
            prepend-icon="mdi-account-group"
            item-text="name"
            item-value="uid"
            clearable
            :disabled="loadingOrders"
            @click:clear="searchValueCustomerUid = null"
          />
        </v-col>
        <v-col
          cols="12"
          lg="3"
          sm="6"
        >
          <v-menu
            ref="datemenu"
            v-model="datemenu"
            :close-on-content-click="false"
            :return-value.sync="searchDates"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="searchDatesText"
                :label="$t('Pages.ResellerOrders.Form.Labels.DateFilter')"
                prepend-icon="mdi-calendar"
                :disabled="loadingOrders"
                readonly
                clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              />
            </template>
            <v-date-picker
              v-model="searchDates"
              :max="currentDate"
              color="info"
              no-title
              scrollable
              range
            >
              <v-btn
                text
                @click="datemenu = false"
              >
                {{ $t('General.Buttons.Cancel') }}
              </v-btn>
              <v-btn
                text
                color="info"
                @click="setDatesFilter(searchDates)"
              >
                {{ $t('Pages.ResellerOrders.Form.Buttons.PickDates') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
        <v-col
          cols="12"
          lg="3"
          sm="6"
          class="d-flex"
        >
          <v-text-field
            v-model="searchMinAmount"
            :label="$t('Pages.ResellerOrders.Form.Labels.AmountFilter')"
            :placeholder="$t('Pages.ResellerOrders.Form.Placeholders.MinAmount')"
            prepend-icon="mdi-currency-eur"
            type="number"
            :disabled="loadingOrders"
            clearable
          />
          <v-text-field
            v-model="searchMaxAmount"
            :placeholder="$t('Pages.ResellerOrders.Form.Placeholders.MaxAmount')"
            prepend-icon="mdi-currency-eur"
            type="number"
            :disabled="loadingOrders"
            clearable
          />
        </v-col>
      </v-row>

      <v-row class="text-right">
        <v-spacer />
        <debounce-v-btn
          color="error"
          :disabled="!filtersSelected"
          @click="resetFilters"
        >
          {{ $t('General.Buttons.ClearFilters') }}
        </debounce-v-btn>
        <debounce-v-btn
          color="success"
          @click="loadFilterOrders"
        >
          <v-icon left>
            mdi-filter
          </v-icon> {{ $t('General.Buttons.FilterEntity', ['orders']) }}
        </debounce-v-btn>
      </v-row>

      <v-divider class="mt-3" />

      <v-data-table
        class="row-action"
        :headers="headers"
        :hide-default-header="true"
        :items="orders"
        :sort-desc="[false, false]"
        :server-items-length="paging.totalResultCount"
        :page.sync="currentPage"
        :items-per-page.sync="itemsPerPage"
        :loading="loadingOrders"
        :loading-text="$t('Pages.ResellerOrders.Messages.OrdersLoadingMessage')"
        multi-sort
        @update:page="loadOrders"
        @update:items-per-page="loadOrders"
      >
        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th
                v-for="(header, i) in headers"
                :key="`header-${i}`"
                :class="(i + 1 === headers.length ? 'text-right' : '')"
              >
                {{ $t(header.text) }}
              </th>
            </tr>
          </thead>
        </template>
        <template v-slot:item="{ item }">
          <tr @click="gotoDetails(item)">
            <td>
              <v-btn
                class="px-2 ml-1"
                color="info"
                rounded
                small
                @click="gotoDetails(item)"
              >
                {{ $t('General.Buttons.Details') }}
              </v-btn>
            </td>
            <td>{{ getResellerName(item.resellerId) }}</td>
            <td>{{ getCustomerName(item.customerId) }}</td>
            <td>{{ item.createDate | moment('DD/MM/YYYY HH:mm:ss') }}</td>
            <td class="text-right">
              <span class="mdi mdi-currency-eur" />{{ item.totalAmount.toFixed(2) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </base-material-card>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import adminOrderService from '@/services/Admin/orderService'
  import moment from 'moment'

  export default {
    data: () => ({
      headers: [
        {
          sortable: false,
          text: '',
          width: '1%',
        },
        {
          sortable: true,
          text: 'Pages.ResellerOrders.TableHeaders.ResellerName',
        },
        {
          sortable: true,
          text: 'Pages.ResellerOrders.TableHeaders.CustomerName',
          value: 'customerName',
        },
        {
          sortable: true,
          text: 'Pages.ResellerOrders.TableHeaders.OrderDate',
          value: 'createDate',
        },
        {
          sortable: true,
          text: 'Pages.ResellerOrders.TableHeaders.OrderAmount',
          value: 'totalAmount',
        },
      ],
      orders: [],
      paging: {},
      currentPage: 1,
      itemsPerPage: 10,
      loadingOrders: false,
      searchValueResellerUid: null,
      searchValueCustomerUid: null,
      datemenu: false,
      searchDates: [],
      searchMinAmount: null,
      searchMaxAmount: null,
    }),
    computed: {
      ...mapGetters(['getCustomerName', 'getResellerName']),
      resellers () {
        return this.$store.state.resellers.list
      },
      customers () {
        return this.$store.state.customers.data
      },
      searchDatesText () {
        return this.searchDates.map(d => moment(d).format('DD/MM/YYYY')).join(' - ')
      },
      currentDate () {
        return moment().format('YYYY-MM-DD')
      },
      filtersSelected () {
        return (
          this.searchValueCustomerUid != null ||
          this.searchValueResellerUid != null ||
          this.searchDates.length > 0 ||
          this.searchMinAmount != null ||
          this.searchMaxAmount != null
        )
      },
    },
    created: async function () {
      if (this.$store.state.resellers.list.length === 0) {
        await this.$store.dispatch('RefreshResellers')
      }
      if (this.$store.state.customers.data.length === 0) {
        await this.$store.dispatch('RefreshCustomers')
      }
      this.loadOrders()
    },
    methods: {
      async loadOrders () {
        this.loadingOrders = true

        var dateTimeRange = this.parseDates()
        var pagedOrdersApiResult = await adminOrderService.getOrders(
          this.currentPage - 1,
          this.itemsPerPage,
          this.searchValueCustomerUid,
          this.searchValueResellerUid,
          dateTimeRange,
          this.searchMinAmount,
          this.searchMaxAmount)

        if (pagedOrdersApiResult.success === false) {
          this.loadingOrders = false
        }

        var pagedOrders = pagedOrdersApiResult.value
        this.orders = pagedOrders.data
        if (pagedOrders.headers['x-pagination']) {
          this.paging = JSON.parse(pagedOrders.headers['x-pagination'])
        }
        this.loadingOrders = false
      },
      async loadFilterOrders () {
        this.currentPage = 1
        await this.loadOrders()
      },
      gotoDetails (item) {
        this.$router.push({ name: 'Order', params: { uid: item.uid } })
      },
      clearDates () {
        this.searchDates = []
      },
      setDatesFilter (dates) {
        this.$refs.datemenu.save(dates)
      },
      resetFilters () {
        this.searchValueCustomerUid = null
        this.searchValueResellerUid = null
        this.clearDates()
        this.searchMinAmount = null
        this.searchMaxAmount = null
        this.loadOrders()
      },
      parseDates () {
        var dateTimeRange = []
        if (this.searchDates.length > 0) {
          dateTimeRange.push(moment.utc(this.searchDates[0]).toISOString())
          if (this.searchDates.length === 1) {
            dateTimeRange.push(moment.utc(this.searchDates[0]).endOf('day').toISOString())
          } else {
            dateTimeRange.push(moment.utc(this.searchDates[1]).endOf('day').toISOString())
          }
        }
        return dateTimeRange
      },
    },
  }
</script>
